import React, { useEffect } from "react";
import {
  CreditCard,
  Calendar,
  Users,
  CheckCircle,
  Check,
  X,
  Activity,
  ArrowLeft,
  ArrowRight,
  CheckCheck,
} from "lucide-react";
import translations from "../../../constants/translations";
import StepSelectPeriod from "./StepSelectPeriod";
import StepChooseEmployees from "./StepChooseEmployees";
import StepReviewProcess from "./StepReviewProcess";
import useProcessPayroll from "../../../hooks/useProcessPayroll";

// One neutral palette + a single brand accent. Every key the step components
// read must be defined here — a missing one leaves native form controls
// unstyled, and they then follow the OS color scheme instead of the app theme.
const buildPalette = (isDark) => ({
  primary: "#2e7cba",
  primaryHover: "#256a9f",
  primarySoft: isDark ? "rgba(46,124,186,0.18)" : "#eef5fb",
  success: isDark ? "#34d399" : "#059669",
  successSoft: isDark ? "rgba(52,211,153,0.12)" : "#ecfdf5",
  warning: isDark ? "#fbbf24" : "#b45309",
  warningSoft: isDark ? "rgba(251,191,36,0.10)" : "#fffbeb",
  danger: isDark ? "#f87171" : "#dc2626",
  dangerSoft: isDark ? "rgba(248,113,113,0.10)" : "#fef2f2",
  info: isDark ? "#60a5fa" : "#2563eb",
  infoSoft: isDark ? "rgba(96,165,250,0.10)" : "#eff6ff",
  textPrimary: isDark ? "#f1f5f9" : "#0f172a",
  textSecondary: isDark ? "#cbd5e1" : "#475569",
  textMuted: isDark ? "#94a3b8" : "#64748b",
  border: isDark ? "#475569" : "#cbd5e1",
  borderLight: isDark ? "#334155" : "#e2e8f0",
  surface: isDark ? "#1e293b" : "#ffffff",
  surfaceSubtle: isDark ? "#162033" : "#f8fafc",
  cardBackground: isDark ? "#1e293b" : "#ffffff",
  inputBackground: isDark ? "#0f172a" : "#ffffff",
});

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ProcessPayrollModal = ({
  onClose,
  onProcessComplete,
  payrollData,
  rowsLoading = false,
  selectedEmployees,
  setSelectedEmployees,
  selectedMonth,
  setSelectedMonth,
  theme,
  language,
  API_BASE_URL,
  clientName,
  showAlert,
  processStep,
  setProcessStep,
}) => {
  const isDark = theme === "dark";
  const isAr = language === "ar";
  const c = buildPalette(isDark);
  const currentTheme = { colors: c };
  const t = (key) => translations[language][key] || key;
  const L = (ar, en) => (isAr ? ar : en);

  const modalStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.55)",
      backdropFilter: "blur(3px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
      padding: "16px",
      overflow: "hidden",
    },
    modalContent: {
      background: c.surface,
      border: `1px solid ${c.borderLight}`,
      // Native checkboxes/selects/scrollbars follow the app theme, not the OS.
      colorScheme: isDark ? "dark" : "light",
      borderRadius: "18px",
      width: "100%",
      maxWidth: "1180px",
      height: "calc(100vh - 32px)",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
      overflow: "hidden",
      color: c.textPrimary,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      padding: "22px 32px",
      borderBottom: `1px solid ${c.borderLight}`,
    },
    body: {
      padding: "28px 32px",
      overflowY: "auto",
      flex: 1,
      background: c.surfaceSubtle,
    },
    footer: {
      padding: "18px 32px",
      borderTop: `1px solid ${c.borderLight}`,
      background: c.surface,
    },
    title: {
      color: c.textPrimary,
      fontSize: "22px",
      fontWeight: 700,
      margin: 0,
      lineHeight: 1.25,
    },
    formSection: {
      background: c.surface,
      borderRadius: "14px",
      padding: "28px",
      border: `1px solid ${c.borderLight}`,
      marginBottom: "20px",
    },
    sectionTitle: {
      color: c.textPrimary,
      fontSize: "18px",
      fontWeight: 700,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    sectionSubtitle: {
      color: c.textMuted,
      fontSize: "14px",
      margin: "6px 0 0",
      lineHeight: 1.5,
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    formGroupWithMargin: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginTop: "20px",
    },
    label: {
      color: c.textSecondary,
      fontSize: "13px",
      fontWeight: 600,
    },
    input: {
      background: c.inputBackground,
      border: `1px solid ${c.border}`,
      borderRadius: "10px",
      padding: "12px 14px",
      color: c.textPrimary,
      fontSize: "15px",
      outline: "none",
    },
    checkboxGroup: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    employeeList: {
      maxHeight: "calc(100vh - 420px)",
      overflowY: "auto",
    },
    employeeItem: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      cursor: "pointer",
    },
    previewCard: {
      background: c.surface,
      borderRadius: "14px",
      padding: "24px",
      marginBottom: "20px",
      border: `1px solid ${c.borderLight}`,
    },
    previewTotal: {
      marginTop: "20px",
      padding: "20px 24px",
      background: c.primary,
      color: "#ffffff",
      borderRadius: "12px",
      fontSize: "22px",
      fontWeight: 800,
    },
    actionButtons: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      flexWrap: "wrap",
    },
  };

  const {
    companyConfig,
    companies,
    companyConfigsById,
    companiesLoading,
    companiesError,
    formData,
    setFormData,
    previewData,
    setSelectedAll,
    processingPayroll,
    processingStatus,
    handleInputChange,
    handleEmployeeSelection,
    calculatePreview,
    handleProcessPayroll,
  } = useProcessPayroll({
    isOpen: true,
    onClose,
    selectedMonth,
    onProcessComplete,
    setSelectedMonth,
    showAlert,
    t,
    language,
    selectedEmployees,
    setSelectedEmployees,
    payrollData,
    clientName,
  });

  useEffect(() => {
    if (processStep === 3 && selectedEmployees.length > 0) {
      calculatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processStep, selectedEmployees]);

  const steps = [
    { number: 1, title: t("selectPeriod"), icon: Calendar },
    { number: 2, title: t("chooseEmployees"), icon: Users },
    { number: 3, title: t("reviewProcess"), icon: CheckCircle },
  ];

  const renderStepContent = () => {
    switch (processStep) {
      case 1:
        return (
          <StepSelectPeriod
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            modalStyles={modalStyles}
            currentTheme={currentTheme}
            t={t}
            theme={theme}
            language={language}
            companies={companies}
            companyConfigsById={companyConfigsById}
            companyConfig={companyConfig}
            companiesLoading={companiesLoading}
            companiesError={companiesError}
          />
        );

      case 2:
        return (
          <StepChooseEmployees
            payrollData={payrollData}
            rowsLoading={rowsLoading}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            handleEmployeeSelection={handleEmployeeSelection}
            setSelectedAll={setSelectedAll}
            selectedCompanyIds={formData.companyIds || []}
            modalStyles={modalStyles}
            currentTheme={currentTheme}
            t={t}
            language={language}
            theme={theme}
          />
        );

      case 3:
        return (
          <StepReviewProcess
            previewData={previewData}
            selectedEmployees={selectedEmployees}
            processingPayroll={processingPayroll}
            modalStyles={modalStyles}
            currentTheme={currentTheme}
            t={t}
            language={language}
          />
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (processStep === 3) {
      handleProcessPayroll(false);
    } else {
      setProcessStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setProcessStep((prev) => prev - 1);
  };

  // ── Step 1 validity ──────────────────────────────────────────────────────
  // Multi-company flow: at least one company selected, and every selected
  // company has a period with month/year set.
  // Falls back to the legacy single-period shape when no companies were
  // selected (e.g. companies list hasn't loaded / single-company clients).
  const hasValidMultiCompanySelection = () => {
    const companyIds = formData.companyIds || [];
    if (companyIds.length === 0) return false;

    const periods = formData.periodsByCompany || {};
    return companyIds.every((id) => {
      const period = periods[id];
      return period && period.month && period.year;
    });
  };

  const canProceed = () => {
    switch (processStep) {
      case 1:
        return (
          hasValidMultiCompanySelection() ||
          (formData.payrollPeriod.month && formData.payrollPeriod.year)
        );
      case 2:
        return selectedEmployees.length > 0;
      case 3:
        return previewData && selectedEmployees.length > 0;
      default:
        return false;
    }
  };

  const selectedTotal = selectedEmployees.reduce(
    (sum, emp) => sum + (Number(emp.netSalary) || 0),
    0,
  );

  const BackIcon = isAr ? ArrowRight : ArrowLeft;
  const NextIcon = isAr ? ArrowLeft : ArrowRight;

  const button = (variant, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: "46px",
    padding: "0 22px",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background 0.15s, border-color 0.15s",
    whiteSpace: "nowrap",
    ...(variant === "primary" && {
      background: c.primary,
      color: "#ffffff",
      border: `1px solid ${c.primary}`,
    }),
    ...(variant === "success" && {
      background: isDark ? "#059669" : c.success,
      color: "#ffffff",
      border: `1px solid ${isDark ? "#059669" : c.success}`,
    }),
    ...(variant === "secondary" && {
      background: c.surface,
      color: c.textPrimary,
      border: `1px solid ${c.border}`,
    }),
  });

  return (
    <div
      className="pp-overlay payroll-modal"
      style={modalStyles.modal}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .pp-btn:not(:disabled):hover { filter: brightness(0.94); }
        .pp-btn-secondary:not(:disabled):hover { background: ${c.surfaceSubtle} !important; }
        .pp-close:hover { background: ${c.surfaceSubtle} !important; color: ${c.textPrimary} !important; }
        .pp-field:focus { border-color: ${c.primary} !important; box-shadow: 0 0 0 3px ${c.primarySoft}; }
        .pp-row:hover { background: ${c.surfaceSubtle}; }
        .pp-row[data-selected="true"]:hover { background: ${c.primarySoft}; filter: brightness(0.98); }
        .pp-group-head:hover { background: ${isDark ? "#1b2638" : "#f1f5f9"} !important; }
        .pp-chip:hover { border-color: ${c.primary} !important; }
        .pp-modal input[type="checkbox"] { accent-color: ${c.primary}; }
        @media (max-width: 640px) {
          .pp-overlay { padding: 8px !important; }
          .pp-pad { padding-left: 16px !important; padding-right: 16px !important; }
          .pp-section { padding: 18px !important; }
          .pp-toolbar > * { flex: 1 1 100% !important; margin-inline-start: 0 !important; }
          .pp-step-label { display: none; }
          .pp-footer-summary { display: none !important; }
        }
      `}</style>
      <div
        className="pp-modal"
        style={modalStyles.modalContent}
        dir={isAr ? "rtl" : "ltr"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pp-title"
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div style={modalStyles.header} className="pp-pad">
          <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: c.primarySoft,
                color: c.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CreditCard size={22} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 id="pp-title" style={modalStyles.title}>
                {t("processNewPayroll")}
              </h1>
              <div style={{ fontSize: "13.5px", color: c.textMuted, marginTop: "2px" }}>
                {L(
                  `الخطوة ${processStep} من ${steps.length} — ${steps[processStep - 1]?.title ?? ""}`,
                  `Step ${processStep} of ${steps.length} — ${steps[processStep - 1]?.title ?? ""}`,
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="pp-close"
            onClick={onClose}
            aria-label={L("إغلاق", "Close")}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: c.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
          >
            <X size={22} />
          </button>
        </div>

        {/* ── Stepper ──────────────────────────────────────────────────── */}
        <div
          className="pp-pad"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "18px 32px",
            borderBottom: `1px solid ${c.borderLight}`,
            background: c.surface,
          }}
        >
          {steps.map((step, index) => {
            const isDone = processStep > step.number;
            const isActive = processStep === step.number;
            return (
              <React.Fragment key={step.number}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: 700,
                      background: isDone || isActive ? c.primary : c.surface,
                      color: isDone || isActive ? "#ffffff" : c.textMuted,
                      border: `2px solid ${isDone || isActive ? c.primary : c.border}`,
                      boxShadow: isActive ? `0 0 0 4px ${c.primarySoft}` : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone ? <Check size={16} strokeWidth={3} /> : step.number}
                  </div>
                  <div className="pp-step-label">
                    <div style={{ fontSize: "11.5px", color: c.textMuted, fontWeight: 600 }}>
                      {L(`الخطوة ${step.number}`, `Step ${step.number}`)}
                    </div>
                    <div
                      style={{
                        fontSize: "14.5px",
                        fontWeight: isActive ? 700 : 600,
                        color: isActive || isDone ? c.textPrimary : c.textMuted,
                      }}
                    >
                      {step.title}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: "24px",
                      height: "2px",
                      borderRadius: "2px",
                      background: isDone ? c.primary : c.borderLight,
                      transition: "background 0.2s",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div style={modalStyles.body} className="pp-scroll pp-pad">
          {renderStepContent()}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div style={modalStyles.footer} className="pp-pad">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div
              className="pp-footer-summary"
              style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}
            >
              {processingPayroll && processingStatus ? (
                <span style={{ fontSize: "14px", color: c.textSecondary }}>
                  {processingStatus}
                </span>
              ) : processStep > 1 && selectedEmployees.length > 0 ? (
                <>
                  <span style={{ fontSize: "12.5px", color: c.textMuted, fontWeight: 600 }}>
                    {L(
                      `${selectedEmployees.length} موظف محدد`,
                      `${selectedEmployees.length} employee${selectedEmployees.length === 1 ? "" : "s"} selected`,
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: 700,
                      color: c.textPrimary,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatMoney(selectedTotal)} {t("currency")}
                  </span>
                </>
              ) : null}
            </div>

            <div style={{ ...modalStyles.actionButtons, marginInlineStart: "auto" }}>
              {processStep > 1 && (
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary"
                  onClick={handleBack}
                  disabled={processingPayroll}
                  style={button("secondary", processingPayroll)}
                >
                  <BackIcon size={18} />
                  {t("back")}
                </button>
              )}

              {processStep === 3 ? (
                <>
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    onClick={() => handleProcessPayroll(false)}
                    disabled={!canProceed() || processingPayroll}
                    style={button("secondary", !canProceed() || processingPayroll)}
                  >
                    {processingPayroll ? <Activity size={18} /> : <CheckCircle size={18} />}
                    {processingPayroll ? t("processing") : t("processPayrollBtn")}
                  </button>
                  <button
                    type="button"
                    className="pp-btn"
                    onClick={() => handleProcessPayroll(true)}
                    disabled={!canProceed() || processingPayroll}
                    style={button("success", !canProceed() || processingPayroll)}
                  >
                    {processingPayroll ? <Activity size={18} /> : <CheckCheck size={18} />}
                    {processingPayroll
                      ? t("processing")
                      : L("معالجة وتحديد كمدفوع", "Process & Mark Paid")}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="pp-btn"
                  onClick={handleNext}
                  disabled={!canProceed() || processingPayroll}
                  style={button("primary", !canProceed() || processingPayroll)}
                >
                  {t("nextStep")}
                  <NextIcon size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessPayrollModal;
